import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'promptera-public-theme';
const ThemeContext = createContext(null);

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  const savedTheme = window.localStorage.getItem(STORAGE_KEY);
  return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : getSystemTheme();
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) return undefined;

    const syncSystemTheme = (event) => {
      const savedTheme = window.localStorage.getItem(STORAGE_KEY);
      if (!savedTheme) setTheme(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener?.('change', syncSystemTheme);
    return () => mediaQuery.removeEventListener?.('change', syncSystemTheme);
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme: (nextTheme) => {
      const resolvedTheme = nextTheme === 'light' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, resolvedTheme);
      setTheme(resolvedTheme);
    },
    toggleTheme: () => {
      const nextTheme = theme === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, nextTheme);
      setTheme(nextTheme);
    },
  }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
